import { useState, useEffect, useMemo, SyntheticEvent } from 'react';
import { Button, Alert, Box, Tabs, Tab, useMediaQuery, useTheme, Stack } from '@mui/material';
import {
    AddCircle as AddIcon,
    RemoveCircle as LeaveIcon,
    CheckCircle as CheckIcon,
    AssignmentInd as CategoryRequestIcon,
} from '@mui/icons-material';
import {
    useGetUserInfoQuery,
    useCreateCategoryRequestMutation,
    useGetCategoryRequestsQuery,
    useDeleteCategoryRequestMutation,
} from '@/store/apiSlice';
import { useCategoriesResources } from '@/store/hooks';
import { useI18nContext } from '@/i18n/i18n-react';
import { CategoryRequestsSection } from '@/components/CategoryRequestsSection';
import { CategorySelectionSection } from '@/components/CategorySelectionSection';
import BaseModal from '@/components/modals/BaseModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { close } from '@/store/uiSlice';
import { themeConstants } from '@/theme';

const CategoryRequestModal = () => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const dispatch = useAppDispatch();
    const open = useAppSelector((state) => state.ui.open.categoryRequest);
    const onClose = () => dispatch(close('categoryRequest'));

    const [activeTab, setActiveTab] = useState<'join' | 'leave'>('join');
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
    const [message, setMessage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [withdrawingId, setWithdrawingId] = useState<number | null>(null);

    const categories = useCategoriesResources();
    const { data: userInfo } = useGetUserInfoQuery();
    const { data: requestsData, refetch: refetchRequests } = useGetCategoryRequestsQuery({
        status: 'all',
    });
    const [createRequest, { isLoading }] = useCreateCategoryRequestMutation();
    const [deleteRequest, { isLoading: isDeleting }] = useDeleteCategoryRequestMutation();

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open) {
            refetchRequests();

            setActiveTab('join');
            setSelectedCategoryIds([]);
            setMessage('');
            setError(null);
        }
    }, [open, refetchRequests]);

    const handleTabChange = (_event: SyntheticEvent, newValue: 'join' | 'leave') => {
        setActiveTab(newValue);
        setSelectedCategoryIds([]);
        setError(null);
    };

    const handleCategoryToggle = (categoryId: number) => {
        setSelectedCategoryIds((prev) => {
            if (prev.includes(categoryId)) {
                return prev.filter((id) => id !== categoryId);
            } else {
                return [...prev, categoryId];
            }
        });
        setError(null);
    };

    const handleWithdraw = async (requestId: number) => {
        setWithdrawingId(requestId);
        try {
            await deleteRequest(requestId).unwrap();
        } catch (err: any) {
            const errorMessage =
                err?.data?.message ||
                err?.message ||
                LL.categoryRequests.errorWithdraw({ message: 'Unknown error' });
            setError(errorMessage);
        } finally {
            setWithdrawingId(null);
        }
    };

    const handleSubmit = async () => {
        if (selectedCategoryIds.length === 0) {
            setError(LL.categoryRequests.selectCategory());
            return;
        }

        try {
            // Create requests for all selected categories
            const promises = selectedCategoryIds.map((categoryId) =>
                createRequest({
                    categoryId: categoryId,
                    requestType: activeTab,
                    message: message || undefined,
                }).unwrap()
            );

            await Promise.all(promises);

            // Reset form and close
            setSelectedCategoryIds([]);
            setMessage('');
            setError(null);
            onClose();
        } catch (err: any) {
            const errorMessage =
                err?.data?.message ||
                err?.message ||
                LL.categoryRequests.errorCreate({ message: 'Unknown error' });
            setError(errorMessage);
        }
    };

    // Get user's assigned categories
    const assignedCategoryIds = userInfo?.categoriesAssigned || [];

    // Get pending request category IDs
    const pendingRequestCategoryIds = useMemo(() => {
        return new Set(
            requestsData?.data
                ?.filter((req) => req.status === 'pending')
                .map((req) => req.categoryId) || []
        );
    }, [requestsData]);

    // Get pending and denied requests with category details
    const { pendingRequests, deniedRequests } = useMemo(() => {
        const pending = requestsData?.data?.filter((req) => req.status === 'pending') || [];
        const denied = requestsData?.data?.filter((req) => req.status === 'denied') || [];

        return {
            pendingRequests: pending,
            deniedRequests: denied,
        };
    }, [requestsData]);

    // Filter available categories based on active tab
    const availableCategories = useMemo(() => {
        return (
            categories?.filter((cat) => {
                if (cat.active === 0) return false; // Don't show inactive categories

                if (activeTab === 'join') {
                    // For join requests, show categories user is NOT a member of
                    return !assignedCategoryIds.includes(cat.id);
                } else {
                    // For leave requests, show categories user IS a member of
                    return assignedCategoryIds.includes(cat.id);
                }
            }) || []
        );
    }, [categories, assignedCategoryIds, activeTab]);

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            minHeight={{ xs: '100vh', sm: '60vh' }}
            styledHeader={{
                icon: <CategoryRequestIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: 'white' }} />,
                title: LL.menu.manageCategories(),
                subtitle:
                    selectedCategoryIds.length > 0
                        ? [
                              LL.categoryRequests.selected({
                                  count: selectedCategoryIds.length,
                              }),
                          ]
                        : undefined,
                gradient: themeConstants.gradients.primary,
            }}
            actions={
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ flex: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}
                >
                    {/* Error Display */}
                    {error && (
                        <Alert severity="error" sx={{ flex: 1 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Button onClick={onClose} disabled={isLoading}>
                        {LL.common.cancel()}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={selectedCategoryIds.length === 0 || isLoading}
                        startIcon={<CheckIcon />}
                    >
                        {LL.common.create()}{' '}
                        {selectedCategoryIds.length > 0 && `(${selectedCategoryIds.length})`}
                    </Button>
                </Stack>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Show pending and denied requests */}
                <CategoryRequestsSection
                    pendingRequests={pendingRequests}
                    deniedRequests={deniedRequests}
                    onWithdraw={handleWithdraw}
                    isDeleting={isDeleting}
                    withdrawingId={withdrawingId}
                />

                {/* Tabs for Join/Leave */}
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    centered={!isMobile}
                    variant="scrollable"
                >
                    <Tab
                        label={LL.categoryRequests.requestJoin()}
                        value="join"
                        icon={<AddIcon />}
                        iconPosition="start"
                    />
                    <Tab
                        label={LL.categoryRequests.requestLeave()}
                        value="leave"
                        icon={<LeaveIcon />}
                        iconPosition="start"
                    />
                </Tabs>

                {/* Category Selection */}
                <CategorySelectionSection
                    availableCategories={availableCategories}
                    selectedCategoryIds={selectedCategoryIds}
                    pendingRequestCategoryIds={pendingRequestCategoryIds}
                    activeTab={activeTab}
                    message={message}
                    onCategoryToggle={handleCategoryToggle}
                    onMessageChange={setMessage}
                />
            </Box>
        </BaseModal>
    );
};

export default CategoryRequestModal;
